import { GPT_EVENTS } from './events';
import { AdStats } from './adstats';
import { isDebug, random } from '../helpers/utils';
import { isBlog, getSiteName, getSubdomainAdwatchName } from '../config/domain';
import amaLog, { amaWarn } from '../helpers/log';
import { COLLECTOR } from './constants';
import statssettings from './statssettings';
import { prebidstats } from './prebidstats';
import { videostats } from './videostats';
import { consentstats } from './consentstats';
import { perfEventCollector } from './perfEvent';

interface Handler {
  event: string;
  decorate?: Function;
}

interface Targeting {
  [key: string]: string[];
}

interface Slot {
  getTargeting: (key: string) => string[];
}

const gptEventHandlers: Handler[] = [
  {
    event: GPT_EVENTS.slotRequested,
  },
];

const getHandlersForEvent = (gptEvent: string): Handler[] =>
  gptEventHandlers.filter((handler) => handler.event === gptEvent);

const isValidTargeting = (targeting: string[]): boolean => Array.isArray(targeting) && targeting.length > 0;

const getPositionFromSlot = (slot: Slot): string | undefined => {
  const targeting = slot.getTargeting('pos');
  return isValidTargeting(targeting) ? targeting[0] : undefined;
};

const slotStat = (position: string, gptEvent: string) => ({ placement: position, gptEvent });

const handlerHasDecorator = (handler: Handler): boolean => handler.decorate && typeof handler.decorate === 'function';

const onGptEvent = (targetEvent: string) => (gptEvent: { slot: Slot }) => {
  try {
    const pos = getPositionFromSlot(gptEvent.slot);

    // Track first slot requested for perf telemetry
    if (targetEvent === GPT_EVENTS.slotRequested) {
      const wasMarkedNow = perfEventCollector.markFirstSlotRequested(pos);
      if (wasMarkedNow) {
        perfEventCollector.emitIfReady();
      }
    }

    const stat = slotStat(pos, targetEvent);
    const handlers = getHandlersForEvent(targetEvent);
    const stats = handlers.map((handler) => (handlerHasDecorator(handler) ? handler.decorate!(stat) : stat));

    window.ama?.adStats.add(stats);
  } catch (e) {
    amaLog(`onGptEvent:: Unable to process stats for ${targetEvent}`);
  }
};

const addEventListeners = (): void => {
  window.googletag.pubads().addEventListener(GPT_EVENTS.slotRequested, onGptEvent(GPT_EVENTS.slotRequested));
};

const removeEventListeners = (): void => {
  window.googletag.pubads().removeEventListener(GPT_EVENTS.slotRequested, onGptEvent(GPT_EVENTS.slotRequested));
};

const isWithinSamplerate = (): boolean => random(0, 100) <= (statssettings.get() as any)['samplerate'];
const isAllowedToCollect = (): boolean =>
  isDebug() || ((statssettings.get() as any)['enabled'] && isWithinSamplerate());

let pushInterval: number;
let active: boolean = false;

export const collector = {
  start: (options: any = {}): void => {
    const { useStatsCollector, hasConsent, reset = false } = options;

    if (active && reset === false) return;
    active = true;

    statssettings.get(useStatsCollector);

    const whitelistedBlogs = (statssettings.get() as any)['whitelistedBlogs'];
    const blog = isBlog();
    if (blog && !whitelistedBlogs.includes(window.location.host)) {
      amaLog(`adWatch:: ${location.host} is a not whitelisted subdomain`);
    } else if (isAllowedToCollect()) {

      if (!window.ama) {
        amaWarn('window.ama is not defined, Ad stats (Adwatch) collector will not start');
        return;
      }

      const site = blog ? getSubdomainAdwatchName() : getSiteName();
      window.ama.adStats = new (AdStats as any)(site);
      window.googletag.cmd.push(addEventListeners);

      pushInterval = setInterval(window.ama?.adStats.push, COLLECTOR.pushInterval) as unknown as number;

      amaLog(`stats collector calling prebid and video stats`);

      prebidstats(reset);
      videostats(reset);
      consentstats(hasConsent);
    } else {
      amaLog('stats collector not within samplerate');
    }
  },
  stop: (): void => {
    removeEventListeners();
    clearInterval(pushInterval);
    active = false;
  },
};
